import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../context/UserContextProvider';
import { searchAgents } from '../../api/search';
import Pagination from '../SweepsAccountManagement/Pagination';
import { addToList, updateList } from '../../api/admin';
import DesignateAgentModal from './DesignateAgentModal';

const styles = {
    sectionLabel: {
        textTransform: 'uppercase',
        fontSize: '1.5rem',
        fontWeight: '600',
        color: '#555',
        letterSpacing: '0.05em',
        marginBottom: '8px',
        marginTop: '24px',
        display: 'block'
    },
    card: {
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        backgroundColor: '#fff'
    },
    principalBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '16px',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid #eee',
        flexWrap: 'wrap'
    }
};

const CSR_PAGE_SIZE = 10;

const buildRequest = (agencyInfo, producers) => ({
    sob: agencyInfo?.agencyId,
    name: agencyInfo?.name,
    nationalProducerNumber: agencyInfo?.npn,
    federalEmployerIdentificationNumber: agencyInfo?.fein,
    profileType: 'Agency',
    listType: 'BindingAuthority',
    listStatus: true,
    modifiedByUserId: agencyInfo?.agentId,
    modifiedDate: new Date().toISOString(),
    bindingAuthority: {
        optInStatus: true,
        configurationLevel: 'Agency',
        agents: producers.map(p => ({
            agentId: p.agentId,
            isDefault: p.isDefault === true,
            assignedUnlicensedProducers: Array.isArray(p.assignedUnlicensedProducers)
                ? p.assignedUnlicensedProducers
                : []
        }))
    }
});

export default function CsrSupervisorAssignments({
    fetchList = () => { },
    principalId,
    principalName,
    designatedProducers,
    setDesignatedProducers,
    allApiProducers,
    agencyInfo,
    isOnListRef,
    listRecordIdRef,
    showCsrAssignError,
    setShowCsrAssignError,
    csrAssignSuccessToastRef
}) {
    const user = useContext(UserContext);

    const [rawCsrAgents, setRawCsrAgents] = useState([]);
    const [csrAssignments, setCsrAssignments] = useState([]);
    const [csrFilter, setCsrFilter] = useState('');
    const [assignToAll, setAssignToAll] = useState(false);
    const [csrPage, setCsrPage] = useState(1);
    const [csrViewAll, setCsrViewAll] = useState(false);
    const [designateModalCsr, setDesignateModalCsr] = useState(null);

    // Fetch CSR agents
    useEffect(() => {
        if (!user?.agencyId) return;
        searchAgents(
            { agencyId: user.agencyId, jobProfile: 'Concierge' },
            (response) => { setRawCsrAgents(Array.isArray(response) ? response : []); }
        );
    }, [user?.agencyId]);

    // Rebuild csrAssignments when designatedProducers or rawCsrAgents change
    useEffect(() => {
        const supervisorByCsrId = new Map();
        for (const p of designatedProducers) {
            if (!Array.isArray(p.assignedUnlicensedProducers)) continue;
            const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
            for (const csrId of p.assignedUnlicensedProducers) {
                supervisorByCsrId.set(csrId, name);
            }
        }
        setCsrAssignments(rawCsrAgents.map(a => ({
            id: a.id,
            name: `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim(),
            supervisingAgent: supervisorByCsrId.get(a.id) ?? null
        })));
    }, [rawCsrAgents, designatedProducers]);

    const callApi = (request, onSuccess, onError) => {
        if (isOnListRef.current && listRecordIdRef.current) {
            updateList({ ...request, id: listRecordIdRef.current }, onSuccess, onError);
        } else {
            addToList(request, (response) => {
                listRecordIdRef.current = response?.id ?? listRecordIdRef.current;
                isOnListRef.current = true;
                onSuccess(response);
            }, onError);
        }
    };

    // --- Assign individual agent ---
    const handleAssign = (selectedProducerId) => {
        const selectedApiProducer = allApiProducers.find(p => p.agentSob === selectedProducerId);
        if (!selectedApiProducer || !designateModalCsr) return;

        const csrId = designateModalCsr.id;

        let updatedProducers = designatedProducers.map(p => {
            const existing = Array.isArray(p.assignedUnlicensedProducers) ? p.assignedUnlicensedProducers : [];
            if (p.agentId === selectedProducerId) {
                return {
                    ...p,
                    assignedUnlicensedProducers: existing.includes(csrId) ? existing : [...existing, csrId]
                };
            }
            return { ...p, assignedUnlicensedProducers: existing.filter(id => id !== csrId) };
        });

        const alreadyDesignated = designatedProducers.some(p => p.agentId === selectedProducerId);
        if (!alreadyDesignated) {
            updatedProducers = [
                ...updatedProducers,
                {
                    agentId: selectedApiProducer.agentSob,
                    firstName: selectedApiProducer.firstName,
                    lastName: selectedApiProducer.lastName,
                    nationalProducerNumber: selectedApiProducer.npn,
                    emailAddress: selectedApiProducer.emailAddress ?? selectedApiProducer.email ?? '',
                    isDefault: selectedProducerId === principalId,
                    linesOfBusiness: ['PersonalAuto', 'Commercial', 'Cycle', 'Umbrella'],
                    assignedUnlicensedProducers: [csrId]
                }
            ];
        }

        const producerName = `${selectedApiProducer.firstName ?? ''} ${selectedApiProducer.lastName ?? ''}`.trim();
        const updatedAssignments = csrAssignments.map(c =>
            c.id === csrId ? { ...c, supervisingAgent: producerName } : c
        );

        callApi(
            buildRequest(agencyInfo, updatedProducers),
            () => {
                setShowCsrAssignError(false);
                setDesignatedProducers(updatedProducers);
                setCsrAssignments(updatedAssignments);
                setDesignateModalCsr(null);
                csrAssignSuccessToastRef.current?.show();
                fetchList();
            },
            (error) => {
                console.error(error);
                setDesignateModalCsr(null);
                setShowCsrAssignError(true);
            }
        );
    };

    // --- Assign to All toggle ---
    const handleAssignToAllToggle = () => {
        if (!principalId) return;
        const principalProducer = designatedProducers.find(p => p.agentId === principalId);
        const turningOn = !assignToAll;

        let updatedProducers;
        if (turningOn) {
            const allCsrIds = csrAssignments.map(c => c.id);
            if (principalProducer) {
                updatedProducers = designatedProducers.map(p => {
                    if (p.agentId === principalId) {
                        return { ...p, assignedUnlicensedProducers: allCsrIds };
                    }
                    const existing = Array.isArray(p.assignedUnlicensedProducers) ? p.assignedUnlicensedProducers : [];
                    return { ...p, assignedUnlicensedProducers: existing.filter(id => !allCsrIds.includes(id)) };
                });
            } else {
                const apiProducer = allApiProducers.find(p => p.agentSob === principalId);
                if (!apiProducer) return;
                updatedProducers = [
                    ...designatedProducers.map(p => {
                        const existing = Array.isArray(p.assignedUnlicensedProducers) ? p.assignedUnlicensedProducers : [];
                        return { ...p, assignedUnlicensedProducers: existing.filter(id => !allCsrIds.includes(id)) };
                    }),
                    {
                        agentId: apiProducer.agentSob,
                        firstName: apiProducer.firstName,
                        lastName: apiProducer.lastName,
                        nationalProducerNumber: apiProducer.npn,
                        emailAddress: apiProducer.emailAddress ?? apiProducer.email ?? '',
                        isDefault: true,
                        linesOfBusiness: ['PersonalAuto', 'Commercial', 'Cycle', 'Umbrella'],
                        assignedUnlicensedProducers: allCsrIds
                    }
                ];
            }
        } else {
            updatedProducers = designatedProducers.map(p => {
                if (p.agentId === principalId) {
                    return { ...p, assignedUnlicensedProducers: [] };
                }
                return p;
            });
        }

        const principalCsrIds = new Set(
            Array.isArray(principalProducer?.assignedUnlicensedProducers) ? principalProducer.assignedUnlicensedProducers : []
        );
        const updatedAssignments = turningOn
            ? csrAssignments.map(c => ({ ...c, supervisingAgent: principalName }))
            : csrAssignments.map(c => principalCsrIds.has(c.id) ? { ...c, supervisingAgent: null } : c);

        callApi(
            buildRequest(agencyInfo, updatedProducers),
            () => {
                setShowCsrAssignError(false);
                setDesignatedProducers(updatedProducers);
                setAssignToAll(turningOn);
                setCsrAssignments(updatedAssignments);
                csrAssignSuccessToastRef.current?.show();
                fetchList();
            },
            (error) => {
                console.error(error);
                setShowCsrAssignError(true);
            }
        );
    };

    // --- Filtering / pagination ---
    const filteredCsrs = csrAssignments.filter(c =>
        !csrFilter || c.name?.toLowerCase().includes(csrFilter.toLowerCase())
    );
    const csrTotalPages = Math.ceil(filteredCsrs.length / CSR_PAGE_SIZE);
    const paginatedCsrs = csrViewAll
        ? filteredCsrs
        : filteredCsrs.slice((csrPage - 1) * CSR_PAGE_SIZE, csrPage * CSR_PAGE_SIZE);

    return (
        <>
            {showCsrAssignError && (
                <gds-alert>
                    <gds-alert-message type="danger">
                        Changes Not Saved{' '}
                        <span slot="content">
                            The changes you made were not saved because of a technical problem. Please try again later. If you continue to experience this issue, contact our help desk at 1-800-624-2513 for further assistance.
                        </span>
                    </gds-alert-message>
                </gds-alert>
            )}

            <span style={styles.sectionLabel}>CSR Supervisor Assignments</span>
            <div style={styles.card}>
                {/* Principal bar */}
                <div style={styles.principalBar}>
                    <span style={{ fontWeight: '600' }}>
                        Principal: <span style={{ fontWeight: '400' }}>{principalName}</span>
                    </span>
                    <span style={{ color: '#ccc' }}>|</span>
                    <span style={{ fontSize: '1.3rem', fontWeight: '600' }}>
                        Assign to All Customer Service Representative
                    </span>
                    <gds-switch-toggle
                        size="small"
                        aria-label="Toggle switch (small)"
                        checked={assignToAll || undefined}
                        disabled={!principalId || undefined}
                        onClick={handleAssignToAllToggle}
                    />
                </div>

                {/* Filter */}
                <div style={{ display: 'flex', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                        <gds-input-wrapper size="large" style={{ display: 'block', width: '100%' }}>
                            <gds-label-legend element="label" slot="label" for="filter-csrs">
                                Filter List
                            </gds-label-legend>
                            <gds-icon slot="left-visual" icon-type="filter" size="small" />
                            <input
                                slot="input"
                                type="search"
                                id="filter-csrs"
                                name="filter-csrs"
                                placeholder="Search..."
                                value={csrFilter}
                                onChange={e => { setCsrFilter(e.target.value); setCsrPage(1); }}
                                style={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </gds-input-wrapper>
                    </div>
                </div>

                {/* Table */}
                <gds-table>
                    <gds-table-thead>
                        <gds-table-tr>
                            <gds-table-th style={{ width: '70%' }}>Customer Service <br /> Representative</gds-table-th>
                            <gds-table-th style={{ width: '30%' }}>Designated Supervising <br /> Licensed Agent</gds-table-th>
                        </gds-table-tr>
                    </gds-table-thead>
                    <gds-table-tbody>
                        {paginatedCsrs.length === 0 ? (
                            <gds-table-tr>
                                <gds-table-td colSpan="2" style={{ textAlign: 'center', color: '#888', padding: '16px' }}>
                                    No CSR supervisor assignments.
                                </gds-table-td>
                            </gds-table-tr>
                        ) : paginatedCsrs.map(csr => (
                            <gds-table-tr key={csr.id}>
                                <gds-table-td>{csr.name}</gds-table-td>
                                <gds-table-td>
                                    {csr.supervisingAgent ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>{csr.supervisingAgent}</span>
                                            <button
                                                type="button"
                                                style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, margin: 0, display: 'flex', alignItems: 'center', lineHeight: 0 }}
                                                aria-label={`Edit supervising licensed agent for ${csr.name}`}
                                                onClick={() => setDesignateModalCsr(csr)}
                                            >
                                                <gds-icon
                                                    icon-type="edit"
                                                    color="action"
                                                    size="small"
                                                />
                                            </button>
                                        </span>
                                    ) : (
                                        <gds-button
                                            appearance="secondary"
                                            size="small"
                                            type="button"
                                            aria-label={`Designate supervising licensed agent for ${csr.name}`}
                                            onClick={() => setDesignateModalCsr(csr)}
                                        >
                                            Designate Supervising Licensed Agent &#x2197;
                                        </gds-button>
                                    )}
                                </gds-table-td>
                            </gds-table-tr>
                        ))}
                    </gds-table-tbody>
                </gds-table>

                {filteredCsrs.length > 0 && (
                    <Pagination
                        totalPages={csrTotalPages}
                        currentPage={csrPage}
                        handlePageChange={(page) => setCsrPage(Math.min(Math.max(1, page), Math.max(1, csrTotalPages)))}
                        handleViewAll={() => setCsrViewAll(v => !v)}
                        viewAll={csrViewAll}
                    />
                )}
            </div>

            <DesignateAgentModal
                isOpen={!!designateModalCsr}
                csr={designateModalCsr}
                allApiProducers={allApiProducers}
                onClose={() => setDesignateModalCsr(null)}
                onAssign={handleAssign}
            />
        </>
    );
}
